import { Component, OnInit, OnDestroy, HostListener } from '@angular/core'
import {
  MarketService,
  IProductListing,
} from '../../../services/market.service'
import { ActivatedRoute, Router } from '@angular/router'
import { IProductDetails } from '../../../models/product.model'
import { TranslateService } from '@ngx-translate/core'
import { map, take } from 'rxjs/operators'
import { AuthenticationService } from '../../../services/authentication.service'
import { SnackbarService } from '../../../services/snackbar.service'
import { Title } from '@angular/platform-browser'
import { PaymentService } from '../../../services/payment.service'
import { ProductService } from '../../../services/product.service'
import { SeoService } from '../../../services/seo.service'
import { UtilsService } from '../../../services/utils.service'
import { ProjectService } from '../../../services/project.service'
import { IBreadCrumb } from '../../../models/breadcrumb.model'
import { IProject } from '../../../models/project.model'
import { environment } from '../../../../environments/environment'
import { landingPageJSONLD } from '../../../../assets/schemas/landing-page'

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnInit, OnDestroy {
  loggedIn = this.auth.currentUser
  breadCrumbs: IBreadCrumb[] = []
  projects: IProject[] = []
  price: number
  discountedPrice: number
  discount: number
  priceText: string
  discountedPriceText: string
  isProjectInputOpen = false
  productDetailsLoaded = false

  constructor(
    private activatedRoute: ActivatedRoute,
    private marketService: MarketService,
    private auth: AuthenticationService,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private title: Title,
    private payment: PaymentService,
    private productService: ProductService,
    private seoService: SeoService,
    private projectService: ProjectService,
    private utils: UtilsService
  ) {}

  product: IProductDetails
  recommendations: IProductListing[]
  roundedAvailability: any
  availability: any

  async ngOnInit() {
    this.auth.currentUser.subscribe(async (user) => {
      if (user) {
        this.projects = await this.projectService
          .listProjects()
          .toPromise()
          .then((result: IProject[]) =>
            result.sort((a, b) => a.name.localeCompare(b.name))
          )
      }
    })
    this.activatedRoute.params.subscribe((param: any) => {
      this.marketService
        .product(param.id, true, this.translate.currentLang)
        .pipe(take(1))
        .subscribe(
          (product) => {
            if (product) {
              // this.updatePriceInformation(product)
              this.title.setTitle(
                this.seoService.trimTitleToLength(product.name)
              )
              this.product = product
              this.createBreadcrumb([{ label: product.name, url: '' }])
              this.setMetadata()
              this.productDetailsLoaded = true
            }
          },
          (err) => {
            this.productDetailsLoaded = true
            this.snackbarService.showSnackbar(
              'error.error_retrieving',
              'error.button.close',
              'error-snack'
            )
          }
        )
      this.payment
        .getAvailability(param.id)
        .pipe(take(1))
        .subscribe(
          (result) => {
            this.availability = result
            this.roundAvailability(result)
          },
          (err) => {
            this.snackbarService.showSnackbar(
              'error.error_retrieving',
              'error.button.close',
              'error-snack'
            )
          }
        )
    })
  }

  ngOnDestroy() {
    const lang = this.translate.currentLang
    this.seoService.setJsonLD(landingPageJSONLD[lang])
  }

  createBreadcrumb(data: IBreadCrumb[]) {
    this.breadCrumbs = []
    this.breadCrumbs.push({ label: 'home.breadcrumb.home', url: '/' })
    this.breadCrumbs.push({
      label: 'marketplace.breadcrumb.marketplace',
      url: '/buy',
    })
    this.breadCrumbs = this.breadCrumbs.concat(data)
  }

  setMetadata() {
    const JsonLD = this.createProductJsonLD(this.product)
    this.seoService.createDefaultProductMetaTags(this.product)
    this.seoService.setJsonLD(JsonLD)
  }

  createProductJsonLD(product: IProductDetails) {
    const productUrl = this.utils.getFullResolvedUrl(
      this.activatedRoute.snapshot
    )
    // isSimilarTo is pending (I don't have this info)
    if (!product.price) {
      return {}
    }
    const JsonLD = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      url: `${environment.origin}/${productUrl}`,
      image: ['https://www.li-x.com/assets/img/product.png'],
      offers: {
        '@type': 'Offer',
        priceCurrency: 'EUR',
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: [
            'DE',
            'AT',
            'CH',
            'LI',
            'LU',
            'BE',
            'NL',
            'DK',
            'FR',
            'IT',
            'ES',
            'PT',
            'SE',
            'FI',
            'NO',
            'PL',
            'CZ',
            'SK',
            'HU',
            'SI',
            'HR',
            'RO',
            'BG',
            'EE',
            'LV',
            'LT',
            'IE',
            'GB',
            'GR',
            'CY',
            'MT',
          ],
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency: 'EUR',
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: [
              'DE',
              'AT',
              'CH',
              'LI',
              'LU',
              'BE',
              'NL',
              'DK',
              'FR',
              'IT',
              'ES',
              'PT',
              'SE',
              'FI',
              'NO',
              'PL',
              'CZ',
              'SK',
              'HU',
              'SI',
              'HR',
              'RO',
              'BG',
              'EE',
              'LV',
              'LT',
              'IE',
              'GB',
              'GR',
              'CY',
              'MT',
            ],
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 0,
              unitCode: 'DAY',
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 0,
              unitCode: 'DAY',
            },
          },
        },
        url: `${environment.origin}/${productUrl}`,
        itemCondition: 'https://schema.org/UsedCondition',
        availableDeliveryMethod:
          'http://purl.org/goodrelations/v1#DeliveryModeDirectDownload',
        advanceBookingRequirement: 'Delivery directly after ordering',
        seller: {
          '@type': 'Organization',
          name: 'li-x GmbH',
          url: `${environment.origin}/${this.translate.currentLang}`,
        },
      },
    }
    if (product.description) {
      JsonLD['description'] = product.description
    }
    if (product['info']) {
      product['info'].forEach((e) => {
        if (e.key === 'release') {
          return (JsonLD['releaseDate'] = e.value)
        }
      })
    }
    if (product['class']) {
      JsonLD['category'] = this.translate.instant(
        'marketplace.class.' + product['class']
      )
    }
    if (product['id']) {
      JsonLD['productID'] = product['id']
    }
    if (product.name) {
      JsonLD['name'] = product.name
    }
    if (product.vendor) {
      JsonLD['brand'] = {
        '@type': 'Brand',
        name: product.vendor,
      }
    }
    if (product.price) {
      JsonLD.offers['price'] = product.price
    }
    if (product.available) {
      JsonLD.offers['availability'] = 'https://schema.org/InStock'
    } else {
      JsonLD.offers['availability'] = 'https://schema.org/OutOfStock'
    }
    return JsonLD
  }

  roundAvailability(amount: any) {
    if (amount.availability <= 0) {
      this.translate
        .get('add_cart.available_info.zero_none')
        .subscribe((text: string) => {
          this.roundedAvailability = text
        })
    } else if (amount.availability <= 10) {
      this.translate
        .get('add_cart.available_info.less10')
        .subscribe((text: string) => {
          this.roundedAvailability = text
        })
    } else if (amount.availability <= 100) {
      this.translate
        .get('add_cart.available_info.less100')
        .subscribe((text: string) => {
          this.roundedAvailability = text
        })
    } else {
      this.translate
        .get('add_cart.available_info.more100')
        .subscribe((text: string) => {
          this.roundedAvailability = text
        })
    }
  }

  getProductInfoValue(key, value) {
    const langkey = `product_info.${key}.${value}`
    return map((v) => (v !== langkey ? v : value))(this.translate.get(langkey))
  }

  urlName(product) {
    return this.productService.productUrl(product.name, product.vendor_name)
  }

  updatePriceInformation(product) {
    this.productService.getPriceInformation(product).subscribe((prices) => {
      product.price = prices.price
      product.discount = prices.discount
      product.discountedPrice = prices.discountedPrice
      product.priceText = prices.priceText
      product.discountedPriceText = prices.discountedPriceText
    })
  }

  openProjectList(event) {
    event.stopPropagation()
    this.isProjectInputOpen = true
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    event.stopPropagation()
    if (this.isProjectInputOpen) {
      this.isProjectInputOpen = false
    }
  }
}
